import { useMemo } from 'react';
import { useTranslation } from '@sib/i18n';
import { Button } from '@sib/ui';
import { Edit } from '@sib/ui/icons';
import { DetailsView } from '@sidecar/common/components/DetailsModal';
import {
  TaskActivityLogTabContent,
  TaskCommentsTabContent,
  TaskDetailsTabContent,
  TaskSubtasksTabContent,
} from './Tabs';
import type {
  TaskDetailsNavigation,
  TaskDetailsViewContentProps,
} from './TaskDetailsView.types';
import {
  TaskDetailsTab,
  useCompleteTask,
  useTaskActivityLogCount,
  useTaskDetailsTabs,
} from './TaskDetailsView.utils';

interface TaskDetailsViewPanelsProps extends TaskDetailsViewContentProps {
  navigation: TaskDetailsNavigation;
}

export const TaskDetailsViewPanels = ({
  task,
  onClose,
  onEdit,
  onCreateNextStep,
  navigation,
}: TaskDetailsViewPanelsProps) => {
  const { t } = useTranslation();
  const activityLogCount = useTaskActivityLogCount(task.uuid);
  const tabs = useTaskDetailsTabs({ task, activityLogCount });
  const { canEdit } = task;
  const cannotEditTooltip = t('components.taskDetails.cannotEditTooltip');
  const { completeTask, isCompleting } = useCompleteTask({
    taskId: task.uuid,
  });
  const {
    completeTask: completeAndAddNextStep,
    isCompleting: isCompletingWithNextStep,
  } = useCompleteTask({
    taskId: task.uuid,
    onCompleted: () => onCreateNextStep?.(task.uuid),
  });
  const isCompletionPending = isCompleting || isCompletingWithNextStep;
  const {
    tabRoutes,
    fallbackTab,
    detailsViewKey,
    navigateToSubtasksList,
    navigateToNewSubtask,
    navigateToEditSubtask,
    navigateToCommentsList,
    navigateToNewComment,
    handleTabChange,
  } = navigation;

  const isSubtaskFormOpen =
    tabRoutes.isAddingSubtask || Boolean(tabRoutes.editingSubtaskId);

  const actionsVisibleFor = useMemo(() => {
    if (isSubtaskFormOpen)
      return tabs
        .map(({ value }) => value)
        .filter(value => value !== TaskDetailsTab.Subtasks);

    if (tabRoutes.isAddingComment)
      return tabs
        .map(({ value }) => value)
        .filter(value => value !== TaskDetailsTab.Comments);

    return null;
  }, [isSubtaskFormOpen, tabRoutes.isAddingComment, tabs]);

  return (
    <DetailsView
      key={detailsViewKey}
      tabs={tabs}
      fallbackTab={fallbackTab}
      onTabChange={handleTabChange}
      panels={{
        [TaskDetailsTab.Details]: <TaskDetailsTabContent task={task} />,
        [TaskDetailsTab.Subtasks]: (
          <TaskSubtasksTabContent
            taskId={task.uuid}
            canEdit={canEdit}
            isAdding={tabRoutes.isAddingSubtask}
            editingSubtaskId={tabRoutes.editingSubtaskId}
            onEditSubtask={navigateToEditSubtask}
            onGoBack={navigateToSubtasksList}
          />
        ),
        [TaskDetailsTab.Comments]: (
          <TaskCommentsTabContent
            taskId={task.uuid}
            isAdding={tabRoutes.isAddingComment}
            onNavigateToCommentsList={navigateToCommentsList}
          />
        ),
        [TaskDetailsTab.ActivityLog]: (
          <TaskActivityLogTabContent taskId={task.uuid} />
        ),
      }}
      onClose={onClose}
      onEdit={onEdit}
      closeLabel={t('close')}
      editLabel={t('components.taskDetails.editTask')}
      editDisabled={!canEdit}
      editDisabledTooltip={cannotEditTooltip}
      {...(actionsVisibleFor && { actionsVisibleFor })}
      hideClose
      {...(!task.completed && {
        actions: (
          <div className="flex gap-2">
            <Button
              onClick={completeTask}
              disabled={!canEdit || isCompletionPending}
              loading={isCompleting}
            >
              {t('components.taskDetails.completeTask')}
            </Button>
            {onCreateNextStep && !task.parentTask && (
              <Button
                variant="outline"
                onClick={completeAndAddNextStep}
                disabled={!canEdit || isCompletionPending}
                loading={isCompletingWithNextStep}
              >
                {t('components.taskDetails.createNextStep')}
              </Button>
            )}
          </div>
        ),
      })}
      primaryAction={{
        [TaskDetailsTab.Details]: {
          label: t('components.taskDetails.editTask'),
          onClick: onEdit,
          variant: 'outline',
          icon: <Edit className="size-4" aria-hidden />,
          disabled: !canEdit,
          disabledTooltip: cannotEditTooltip,
        },
        [TaskDetailsTab.Subtasks]: {
          label: t('components.taskDetails.subtasks.addSubtask'),
          onClick: navigateToNewSubtask,
          disabled: !canEdit,
          disabledTooltip: cannotEditTooltip,
        },
        [TaskDetailsTab.Comments]: {
          label: t('components.taskDetails.comments.newComment'),
          onClick: navigateToNewComment,
        },
        [TaskDetailsTab.ActivityLog]: null,
      }}
    />
  );
};
